import { useEffect, useMemo, useState } from 'react'
import { debounce } from 'lodash'
import {
  AttachmentLink,
  Column,
  ColumnValue,
  SavedFilterData,
  SortDirection,
  StatusOption,
  TicketRow,
} from './types'
import {
  buildFilterSearchParams,
  compactFilterData,
  isEmptyFilter,
  isFilterableColumn,
  matchesFilter,
  parseFilterParams,
} from './saved_filters/filter_utils'
import { SYSTEM_DATE_KEYS } from './cell_renderers/types'
import { isStatusColumn } from './column_helpers'

interface UseTableStateProps {
  tickets: TicketRow[]
  columns: Column[]
  visibleColumnKeys?: Set<string>
  availableStatuses: StatusOption[]
  initialFilter?: SavedFilterData
  timeZone?: string
}

interface UseTableStateResult {
  activeFilter: SavedFilterData
  searchTerm: string
  setSearchTerm: (value: string) => void
  sortKey: string | null
  sortDirection: SortDirection
  handleSort: (key: string) => void
  toggleStatusFilter: (status: string) => void
  filteredAndSortedRows: TicketRow[]
  applyFilter: (filter: SavedFilterData, options?: { updateUrl?: boolean }) => void
  clearFilterUrlParams: () => void
}

const SELECT_COLUMN_TYPE = 'Projects::Columns::Select'
const selectValueCollator = new Intl.Collator(undefined, { sensitivity: 'base' })

const isSystemDateColumn = (column?: Column): boolean =>
  column?.type === 'Projects::Columns::System' && SYSTEM_DATE_KEYS.includes(column.key)

const optionLabelMatchesSearch = (column: Column | undefined, rawValue: string, searchLower: string): boolean => {
  if (!column?.column_options) return false

  if (isStatusColumn(column)) {
    const option = (column.column_options as unknown as StatusOption[]).find((optionItem) => optionItem.value === rawValue)
    return option?.label.toLowerCase().includes(searchLower) ?? false
  }

  const option = column.column_options.find((optionItem) => optionItem.label === rawValue)
  return option?.label.toLowerCase().includes(searchLower) ?? false
}

const getInitialSortState = () => {
  const params = new URLSearchParams(window.location.search)
  const order = params.get('order')
  const orderDir = params.get('order_dir')
  return {
    sortKey: order,
    sortDirection: (orderDir === 'desc' ? 'desc' : 'asc') as SortDirection,
  }
}

const updateSortUrlParams = (key: string | null, direction: SortDirection) => {
  const url = new URL(window.location.href)
  if (key) {
    url.searchParams.set('order', key)
    url.searchParams.set('order_dir', direction)
  } else {
    url.searchParams.delete('order')
    url.searchParams.delete('order_dir')
  }
  window.history.replaceState({}, '', url.toString())
}

const updateFilterUrlParams = (filters: SavedFilterData) => {
  const url = new URL(window.location.href)
  const keysToDelete: string[] = []
  url.searchParams.forEach((_, key) => {
    if (key.startsWith('filter[')) keysToDelete.push(key)
  })
  keysToDelete.forEach((key) => url.searchParams.delete(key))

  const filterParams = buildFilterSearchParams(filters)
  filterParams.forEach((value, key) => {
    url.searchParams.append(key, value)
  })

  window.history.replaceState({}, '', url.toString())
}

const clearFilterUrlParams = () => {
  const url = new URL(window.location.href)
  const keysToDelete: string[] = []
  url.searchParams.forEach((_, key) => {
    if (key.startsWith('filter[')) keysToDelete.push(key)
  })
  keysToDelete.forEach((key) => url.searchParams.delete(key))
  window.history.replaceState({}, '', url.toString())
}

const getSelectDisplayValue = (value: unknown, column?: Column): string => {
  if (typeof value === 'number') {
    const optionById = column?.column_options?.find((option) => option.id === value)
    if (optionById) return optionById.label.trim()
  }

  const rawValue = String(value ?? '')
  const optionByValue = column?.column_options?.find((option) => option.label === rawValue)
  return (optionByValue?.label ?? rawValue).trim()
}

const getSelectSortKey = (value: Exclude<ColumnValue, null>, column?: Column): string => {
  if (Array.isArray(value)) {
    return value
      .map((item) => getSelectDisplayValue(item, column))
      .sort((left, right) => selectValueCollator.compare(left, right))
      .join(' | ')
  }

  return getSelectDisplayValue(value, column)
}

export const compareColumnValues = (
  leftValue: Exclude<ColumnValue, null>,
  rightValue: Exclude<ColumnValue, null>,
  column?: Column,
): number => {
  if (column?.type === SELECT_COLUMN_TYPE) {
    return selectValueCollator.compare(
      getSelectSortKey(leftValue, column),
      getSelectSortKey(rightValue, column),
    )
  }

  if (Array.isArray(leftValue) && Array.isArray(rightValue)) {
    return leftValue.length - rightValue.length
  }

  if (typeof leftValue === 'number' && typeof rightValue === 'number') {
    return leftValue - rightValue
  }

  if (column?.type === 'Projects::Columns::Number') {
    return Number(leftValue) - Number(rightValue)
  }

  if (column?.type === 'Projects::Columns::Date' || isSystemDateColumn(column)) {
    return new Date(String(leftValue)).getTime() - new Date(String(rightValue)).getTime()
  }

  return String(leftValue).localeCompare(String(rightValue))
}

const getDefaultFilter = (availableStatuses: StatusOption[]): SavedFilterData => {
  const defaultStatuses = availableStatuses.filter((status) => status.is_default).map((status) => status.value)
  return defaultStatuses.length > 0 ? { status: defaultStatuses } : {}
}

const getInitialActiveFilter = (
  columns: Column[],
  availableStatuses: StatusOption[],
  initialFilter?: SavedFilterData,
): SavedFilterData => {
  const urlFilter = parseFilterParams(columns)
  if (urlFilter !== null) return compactFilterData(urlFilter)
  if (initialFilter) return compactFilterData(initialFilter)
  return getDefaultFilter(availableStatuses)
}

export default function useTableState({
  tickets,
  columns,
  visibleColumnKeys,
  availableStatuses,
  initialFilter,
  timeZone,
}: UseTableStateProps): UseTableStateResult {
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const initialState = getInitialSortState()
  const [sortKey, setSortKey] = useState<string | null>(initialState.sortKey)
  const [sortDirection, setSortDirection] = useState<SortDirection>(initialState.sortDirection)
  const [activeFilter, setActiveFilter] = useState<SavedFilterData>(() =>
    getInitialActiveFilter(columns, availableStatuses, initialFilter)
  )

  const columnsByKey = useMemo(
    () => columns.reduce<Map<string, Column>>((map, column) => {
      map.set(column.key, column)
      return map
    }, new Map()),
    [columns]
  )

  const debouncedSetSearch = useMemo(
    () => debounce((value: string) => setDebouncedSearchTerm(value), 300),
    []
  )

  const handleSearchChange = (value: string) => {
    setSearchTerm(value)
    debouncedSetSearch(value)
  }

  const handleSort = (key: string) => {
    let newDirection: SortDirection = 'asc'
    if (sortKey === key) {
      newDirection = sortDirection === 'asc' ? 'desc' : 'asc'
    }

    setSortKey(key)
    setSortDirection(newDirection)
    updateSortUrlParams(key, newDirection)
  }

  useEffect(() => {
    if (sortKey && visibleColumnKeys && !visibleColumnKeys.has(sortKey)) {
      setSortKey(null)
      updateSortUrlParams(null, sortDirection)
    }
  }, [sortKey, sortDirection, visibleColumnKeys])

  const applyFilter = (filter: SavedFilterData, options?: { updateUrl?: boolean }) => {
    const compactFilter = compactFilterData(filter)
    setActiveFilter(compactFilter)
    if (options?.updateUrl !== false) updateFilterUrlParams(compactFilter)
  }

  const toggleStatusFilter = (status: string) => {
    const currentStatuses = Array.isArray(activeFilter.status) ? activeFilter.status as string[] : []
    const nextStatuses = currentStatuses.includes(status)
      ? currentStatuses.filter((currentStatus) => currentStatus !== status)
      : [...currentStatuses, status]

    const nextFilter = { ...activeFilter }
    if (nextStatuses.length > 0) {
      nextFilter.status = nextStatuses
    } else {
      delete nextFilter.status
    }

    applyFilter(nextFilter)
  }

  const filteredAndSortedRows = useMemo(() => {
    let result = [...tickets]

    Object.entries(activeFilter).forEach(([key, value]) => {
      if (isEmptyFilter(value)) return

      const column = columnsByKey.get(key)
      if (!column || !isFilterableColumn(column)) return

      result = result.filter((ticket) => matchesFilter(ticket, key, value, column, timeZone))
    })

    if (debouncedSearchTerm) {
      const searchLower = debouncedSearchTerm.toLowerCase()
      result = result.filter((ticket) =>
        Object.entries(ticket.values).some(([key, value]) => {
          if (value == null) return false
          if (visibleColumnKeys && !visibleColumnKeys.has(key)) return false

          const column = columnsByKey.get(key)

          if (Array.isArray(value)) {
            return value.some((item) => {
              if (typeof item === 'object' && item !== null && 'name' in item) {
                return (item as AttachmentLink).name?.toLowerCase().includes(searchLower)
              }

              const strValue = String(item)
              if (strValue.toLowerCase().includes(searchLower)) return true

              return optionLabelMatchesSearch(column, strValue, searchLower)
            })
          }

          const strValue = String(value)
          if (strValue.toLowerCase().includes(searchLower)) return true

          if (optionLabelMatchesSearch(column, strValue, searchLower)) return true

          return false
        })
      )
    }

    if (sortKey) {
      const column = columnsByKey.get(sortKey)
      result.sort((left, right) => {
        const leftValue = left.values[sortKey]
        const rightValue = right.values[sortKey]

        if (leftValue == null && rightValue == null) return 0
        if (leftValue == null) return sortDirection === 'asc' ? 1 : -1
        if (rightValue == null) return sortDirection === 'asc' ? -1 : 1

        const comparison = compareColumnValues(leftValue, rightValue, column)
        return sortDirection === 'asc' ? comparison : -comparison
      })
    }

    return result
  }, [tickets, activeFilter, debouncedSearchTerm, sortKey, sortDirection, columnsByKey, timeZone, visibleColumnKeys])

  return {
    activeFilter,
    searchTerm,
    setSearchTerm: handleSearchChange,
    sortKey,
    sortDirection,
    handleSort,
    toggleStatusFilter,
    filteredAndSortedRows,
    applyFilter,
    clearFilterUrlParams,
  }
}
